import { UserSwitchOutlined, WarningOutlined } from '@ant-design/icons'
import { addCommasToNumber, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Popover } from 'antd'
import { DirectivesTable } from './components/DirectivesTable'
import { IntradayProgressBar } from '@components/Reports/DispatchDirective/DirectiveStatus/Intraday/IntradayProgressBar'
import { formatters } from '@utils/intl/dateTimeFormatters'
import dayjs from 'dayjs'
import type { SupplyOptionDirectiveInfo } from './types'

type OrderDirectivePopoverContentProps = {
  supplyOptionInfo: SupplyOptionDirectiveInfo
}

function OrderDirectivePopoverContent({ supplyOptionInfo }: OrderDirectivePopoverContentProps) {
  return (
    <Vertical>
      <Horizontal justifyContent='space-between' alignItems='center' className='mb-2'>
        <Texto appearance='primary' weight='bold'>
          {supplyOptionInfo.directive_name}
        </Texto>
        <Texto weight={supplyOptionInfo.supplier_name ? 600 : 500}>
          <UserSwitchOutlined className='pr-2' /> {supplyOptionInfo.supplier_name || 'Multiple Suppliers'}
        </Texto>
      </Horizontal>
      <Horizontal justifyContent='space-between' alignItems='center' className='mb-3'>
        <Vertical>
          <Texto style={{ fontSize: 12 }}>Est. Load Time:</Texto>
          <Texto weight={'bold'}>{formatters.DATE_TIME(dayjs(supplyOptionInfo.projected_load_date))}</Texto>
        </Vertical>
        <Horizontal>
          <Vertical className='mr-4'>
            <Texto style={{ fontSize: 12 }} align='right'>
              Dir. Min:
            </Texto>
            <Texto weight={'bold'} align='right'>
              {addCommasToNumber(supplyOptionInfo.directive_min)}
            </Texto>
          </Vertical>
          <Vertical>
            <Texto style={{ fontSize: 12 }} align='right'>
              Dir. Max:
            </Texto>
            <Texto weight={'bold'} align='right'>
              {addCommasToNumber(supplyOptionInfo.directive_max)}
            </Texto>
          </Vertical>
        </Horizontal>
      </Horizontal>
      <Horizontal justifyContent='space-between' alignItems='center' className='mb-2'>
        <Vertical>
          <Texto appearance='hint' style={{ fontSize: 12 }}>
            Loaded
          </Texto>
          <Texto appearance='secondary' weight={'bold'}>
            • {addCommasToNumber(supplyOptionInfo.loaded)}
          </Texto>
        </Vertical>
        <Vertical>
          <Texto appearance='hint' style={{ fontSize: 12 }}>
            Carrier
          </Texto>
          <Texto style={{ color: 'var(--theme-color-4)' }} weight={'bold'}>
            • {addCommasToNumber(supplyOptionInfo.carrier)}
          </Texto>
        </Vertical>
        <Vertical>
          <Texto appearance='hint' style={{ fontSize: 12 }}>
            Scheduled
          </Texto>
          <Texto appearance='primary' weight={'bold'}>
            • {addCommasToNumber(supplyOptionInfo.scheduled)}
          </Texto>
        </Vertical>
        <Vertical>
          <Texto appearance='hint' style={{ fontSize: 12 }}>
            Planned
          </Texto>
          <Horizontal alignItems='center'>
            <div className='mr-1 planned-color-indicator' />
            <Texto appearance='secondary' weight={'bold'}>
              {addCommasToNumber(supplyOptionInfo.planned)}
            </Texto>
          </Horizontal>
        </Vertical>
        <Horizontal>
          <Vertical>
            <Texto style={{ fontSize: 12 }} weight={'bold'} align='right'>
              Total{supplyOptionInfo.total_excludes_planned && ' (excludes Planned)'}
            </Texto>
            <Texto weight={'bold'} align='right'>
              {addCommasToNumber(supplyOptionInfo.total)}
            </Texto>
          </Vertical>
        </Horizontal>
      </Horizontal>
      <IntradayProgressBar barHeight={15} directive={supplyOptionInfo} isLoading={false} showPlanned />
      {supplyOptionInfo.alert && (
        <Horizontal className='directive-error-tag p-1 mt-3' verticalCenter horizontalCenter>
          <Texto align='center' style={{ color: 'var(--theme-error)' }} weight={'bold'}>
            <WarningOutlined /> <b>WARNING:</b> DIRECTIVE HAS EXCEEDED MAX
          </Texto>
        </Horizontal>
      )}
      <DirectivesTable group_id={supplyOptionInfo.curve_group_id} projected_load_date={supplyOptionInfo.projected_load_date} />
    </Vertical>
  )
}

export type OrderDirectivePopoverProps = {
  supplyOptionDirectiveInfo: SupplyOptionDirectiveInfo
  children: React.ReactNode
}

export function OrderDirectivePopover({ supplyOptionDirectiveInfo, children }: OrderDirectivePopoverProps) {
  return (
    <Popover
      content={<OrderDirectivePopoverContent supplyOptionInfo={supplyOptionDirectiveInfo} />}
      trigger='hover'
      mouseEnterDelay={0.25}
      overlayStyle={{ width: 550 }}
    >
      {children}
    </Popover>
  )
}
